// ============================================================================
// SettlementModal — Personal Finance V2 (Blueprint Fase 3)
// Modal para transição PENDING → PAID exigindo settlementDate.
// ============================================================================

import React, { useState } from "react";

const fmt = (val) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    val || 0,
  );

export function SettlementModal({ pair, posting, onConfirm, onClose }) {
  const today = new Date().toISOString().split("T")[0];
  const [settlementDate, setSettlementDate] = useState(today);
  const [generateCounterpart, setGenerateCounterpart] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isPair = !!pair;
  const isIncome = !isPair && posting?.group === "RECEITAS";
  const desc = isPair
    ? pair.expense?.description || pair.income?.description || "Par"
    : posting?.description || "Lançamento";
  const value = isPair
    ? pair.expense?.value || pair.income?.value || 0
    : posting?.value || 0;
  const dueDate = isPair
    ? pair.expense?.date || pair.income?.date
    : posting?.date;

  // Guard: não pode liquidar com data futura
  const isFuture = settlementDate > today;

  const handleSubmit = async () => {
    if (isFuture) return;
    setSubmitting(true);
    try {
      await onConfirm({
        settlementDate,
        generateCounterpart: generateCounterpart
          ? { amount: value, description: desc, date: dueDate }
          : false,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md overflow-hidden border border-[#d1d5db] shadow-2xl">
        {/* Title bar — Windows style */}
        <div className="flex items-center justify-between px-4 py-2 bg-[#20243B]">
          <span className="text-white text-[11px] font-bold">
            <i className="fas fa-check-double mr-1.5"></i>
            {isPair ? "Liquidar Par" : "Liquidar Lançamento"}
          </span>
          <button onClick={onClose} className="text-white/60 hover:text-white">
            <i className="fas fa-times text-[11px]"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Summary */}
          <div className="bg-[#f3f4f6] border border-[#e5e7eb] p-3 space-y-1">
            <div className="text-[11px] font-bold text-[#374151]">{desc}</div>
            <div className="flex items-center gap-3 text-[10px] text-[#6b7280]">
              <span>
                <i className="fas fa-calendar mr-1"></i>Vence:{" "}
                {dueDate
                  ? new Date(dueDate + "T12:00:00").toLocaleDateString("pt-BR")
                  : "—"}
              </span>
              <span className="font-mono font-bold text-[12px] text-[#20243B]">
                {fmt(value)}
              </span>
            </div>
            {isPair && (
              <div className="text-[9px] text-[#9ca3af]">
                <i className="fas fa-link mr-0.5"></i>Par:{" "}
                {pair.income?.description} + {pair.expense?.description}
              </div>
            )}
          </div>

          {/* Settlement date */}
          <div>
            <label className="block text-[10px] font-bold text-[#374151] uppercase tracking-wide mb-1">
              <i className="fas fa-calendar-check mr-1"></i>Data do Pagamento
            </label>
            <input
              type="date"
              value={settlementDate}
              onChange={(e) => setSettlementDate(e.target.value)}
              max={today}
              className="w-full px-3 py-1.5 text-[11px] border border-[#c8c8c8] bg-white focus:border-[#20243B] outline-none font-mono"
            />
            {isFuture && (
              <p className="text-[9px] text-[#dc2626] font-bold mt-1">
                <i className="fas fa-exclamation-triangle mr-0.5"></i>
                Data futura não permitida. Só é possível liquidar com data ≤
                hoje.
              </p>
            )}
          </div>

          {/* Checkbox contrapartida (só para despesas PF, não para RECEITAS) */}
          {!isPair && !isIncome && (
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={generateCounterpart}
                  onChange={(e) => setGenerateCounterpart(e.target.checked)}
                  className="w-3.5 h-3.5 accent-[#20243B]"
                />
                <span className="text-[10px] font-medium text-[#374151]">
                  <i className="fas fa-exchange-alt mr-1 text-[#C5A059]"></i>
                  Gerar contrapartida RECEITA (Retirada PJ)
                </span>
              </label>
              {generateCounterpart && (
                <p className="text-[9px] text-[#C5A059] mt-1 ml-5">
                  <i className="fas fa-info-circle mr-0.5"></i>
                  Será criada uma RECEITA de R$ {fmt(value)} para compensar esta
                  despesa.
                </p>
              )}
            </div>
          )}

          {/* Info */}
          <div className="text-[9px] text-[#9ca3af] leading-relaxed">
            <i className="fas fa-info-circle mr-0.5"></i>
            {isPair
              ? "Ambos os lançamentos do par (receita + despesa) serão marcados como PAGO simultaneamente."
              : "O lançamento será marcado como PAGO com a data de pagamento informada."}
          </div>
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-end gap-2 px-4 py-2 bg-[#f3f4f6] border-t border-[#d1d5db]">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-[10px] font-semibold text-[#374151] bg-white border border-[#c8c8c8] hover:bg-[#f3f4f6]"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isFuture || submitting}
            className="px-4 py-1.5 text-[10px] font-bold text-white bg-[#059669] border border-[#059669] hover:bg-[#047857] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <i className="fas fa-spinner fa-spin mr-1"></i>
            ) : (
              <i className="fas fa-check mr-1"></i>
            )}
            Liquidar{isPair ? " Par" : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
